import services from '../services'
import * as types from './mutation-types'

export const GetJourneys = ({ commit }, params) => (
  services.list(params).then(res => {
    commit(types.SET_JOURNEYS, res.data)
  })
)

export const GetJourney = async ({ commit }, id) => {
  commit(types.SET_LOADING, true)
  try {
    const res = await services.show(id)
    commit(types.SET_JOURNEY, res.data.data)
    commit(types.SET_DIRTY, false)
  } finally {
    commit(types.SET_LOADING, false)
  }
}

export const SaveJourney = async ({ commit, state }) => {
  const journey = state.journey
  if (!journey || !journey.name || !journey.name.trim()) {
    throw new Error('Insira o nome da jornada.')
  }

  commit(types.SET_SAVING, true)
  try {
    const nodesPayload = state.nodes.map((node, index) => {
      let parentNodeId = node.parent_node_id || null

      // Resolve temp parent IDs to position references for the backend
      if (parentNodeId && typeof parentNodeId === 'string' && parentNodeId.startsWith('temp_')) {
        const parentNode = state.nodes.find(n => (n.id || n._tempId) === parentNodeId)
        if (parentNode && parentNode.id) {
          parentNodeId = parentNode.id
        } else if (parentNode) {
          const parentIndex = state.nodes.indexOf(parentNode)
          parentNodeId = '_pos:' + parentIndex
        }
      }

      return {
        id: node.id || undefined,
        type: node.type,
        kind: node.kind,
        label: node.label,
        description: node.description,
        config: node.config && !Array.isArray(node.config) ? node.config : {},
        position: index,
        parent_node_id: parentNodeId,
        branch_type: node.branch_type || null
      }
    })

    const payload = {
      name: journey.name.trim(),
      description: journey.description || '',
      nodes: nodesPayload
    }

    const res = await services.save(journey.id || null, payload)
    commit(types.SET_JOURNEY, res.data.data)
    commit(types.SET_DIRTY, false)
    return res
  } finally {
    commit(types.SET_SAVING, false)
  }
}

export const DeleteJourney = async ({ commit }, id) => {
  await services.remove(id)
}

export const DuplicateJourney = async ({ commit }, id) => {
  const res = await services.duplicate(id)
  return res.data.data
}

export const ChangeStatus = async ({ commit, state }, { id, status }) => {
  const res = await services.changeStatus(id, { status })
  if (state.journey && state.journey.id === id) {
    commit(types.SET_JOURNEY, { ...state.journey, status })
  }
  return res
}

export const AddNode = ({ commit }, node) => {
  if (!node._tempId) {
    node._tempId = 'temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
  }
  commit(types.ADD_NODE, node)
}

export const UpdateNode = ({ commit }, node) => {
  commit(types.UPDATE_NODE, node)
}

export const RemoveNode = ({ commit }, nodeId) => {
  commit(types.REMOVE_NODE, nodeId)
}

export const ReorderNodes = ({ commit, state }, nodes) => {
  const branchNodes = state.nodes.filter(n => n.parent_node_id)
  commit(types.SET_NODES, [...nodes, ...branchNodes])
  commit(types.SET_DIRTY, true)
}

export const SelectNode = ({ commit }, nodeId) => {
  commit(types.SET_SELECTED_NODE, nodeId)
}

export const UpdateJourneyField = ({ commit, state }, { field, value }) => {
  if (state.journey) {
    commit(types.SET_JOURNEY, { ...state.journey, [field]: value })
    commit(types.SET_DIRTY, true)
  }
}

export const LoadReferenceData = async ({ commit }) => {
  try {
    const [emailRes, waRes, statusRes, groupRes, tagRes, formRes, journeyRes, fieldTypesRes, refStatusRes, condFieldsRes, followUpRes] = await Promise.allSettled([
      services.getEmailTemplates(),
      services.getWaTemplates(),
      services.getStatuses(),
      services.getGroups(),
      services.getTags(),
      services.getForms({ limit: 100 }),
      services.getJourneysList(),
      services.getFieldTypes(),
      services.getRefStatuses(),
      services.getConditionFields(),
      services.getFollowUpRules()
    ])

    commit(types.SET_REFERENCE_DATA, {
      emailTemplates: emailRes.status === 'fulfilled' ? (emailRes.value.data.data || emailRes.value.data) : [],
      waTemplates: waRes.status === 'fulfilled' ? (waRes.value.data.data || waRes.value.data) : [],
      statuses: statusRes.status === 'fulfilled' ? (statusRes.value.data.data || statusRes.value.data) : [],
      groups: groupRes.status === 'fulfilled' ? (groupRes.value.data.data || groupRes.value.data) : [],
      tags: tagRes.status === 'fulfilled' ? (tagRes.value.data.data || tagRes.value.data) : [],
      forms: formRes.status === 'fulfilled' ? (formRes.value.data.data || formRes.value.data) : [],
      journeysList: journeyRes.status === 'fulfilled' ? (journeyRes.value.data.data || journeyRes.value.data) : [],
      fieldTypes: fieldTypesRes.status === 'fulfilled' ? (fieldTypesRes.value.data.data || fieldTypesRes.value.data) : [],
      refStatuses: refStatusRes.status === 'fulfilled' ? (refStatusRes.value.data.data || refStatusRes.value.data) : {},
      formConditionFields: condFieldsRes.status === 'fulfilled' ? (condFieldsRes.value.data.data || condFieldsRes.value.data) : [],
      followUpRules: followUpRes.status === 'fulfilled' ? (followUpRes.value.data.data || followUpRes.value.data || []) : []
    })
  } catch (e) {
    console.error('Failed to load reference data', e)
  }
}

export const GetAnalytics = async ({ commit }, id) => {
  try {
    const [metricsRes, funnelRes, entriesRes, nodeStatsRes] = await Promise.allSettled([
      services.getMetrics(id),
      services.getFunnel(id),
      services.getEntries(id, { days: 30 }),
      services.getNodeStats(id)
    ])

    commit(types.SET_ANALYTICS, {
      metrics: metricsRes.status === 'fulfilled' ? metricsRes.value.data.data : {},
      funnel: funnelRes.status === 'fulfilled' ? funnelRes.value.data.data : [],
      entries: entriesRes.status === 'fulfilled' ? entriesRes.value.data.data : [],
      nodeStats: nodeStatsRes.status === 'fulfilled' ? nodeStatsRes.value.data.data : []
    })
  } catch (e) {
    console.error('Failed to load analytics', e)
  }
}

export const GetLogs = async ({ commit }, { id, params }) => {
  try {
    const res = await services.getLogs(id, params)
    commit(types.SET_ANALYTICS, { logs: res.data })
  } catch (e) {
    console.error('Failed to load logs', e)
  }
}
